import { timestamp, uuid } from "@/interfaces";
import { individual_payment_status, subs_payment_status } from "@/models/appointment.model";

export type DailyWorkMetaData = {
  chat_room_id?: uuid | null;
} & Record<string, any>;

export enum daily_work_status {
  active = "active",
  paused = "paused",
  cancelled = "cancelled",
}

export enum daily_work_day_status {
  pending = "pending",
  assigned = "assigned",
  accepted = "accepted",
  inprogress = "inprogress",
  completed = "completed",
}

export type day_of_week = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

// New date-based daily work day interface
export interface DailyWorkDay {
  id: uuid;
  daily_work: uuid;
  work_date: string; // date format YYYY-MM-DD
  service: uuid;
  tasks: string[];
  assigned_user: uuid | null;
  assigned_org: uuid | null;
  individual_payment: number;
  individual_payment_status: individual_payment_status;
  subs_payment: number | null;
  subs_payment_status: subs_payment_status | null;
  individual_payment_source: "profile" | "manual" | "none" | null;
  individual_payment_rate_profile: uuid | null;
  individual_payment_rate_applied_at: timestamp | null;
  subs_payment_source: "profile" | "manual" | "none" | null;
  subs_payment_rate_profile: uuid | null;
  subs_payment_rate_applied_at: timestamp | null;
  max_hours?: number | null;
  status: daily_work_day_status;
  schedule_row?: uuid | null;
  schedule_assignment?: uuid | null;
  slot_index?: number | null;
  generation_key?: string | null;
  duration_label?: string | null;
  instructions?: string | null;
  /** Set when a vendor hand-edited a generated day; the master schedule sync no longer touches it. */
  schedule_detached_at?: string | null;
  /** Current hours the database resolved for this completed day. */
  hours_worked?: number | null;
  /** Which input produced `hours_worked`. */
  hours_source?: daily_work_hours_source | null;
  /** Why the clock record could not be used, or why it deserves a check before paying. */
  payout_review?: daily_work_payout_review | null;
  /** `individual_payment` (an hourly rate) x `hours_worked`. Null when hours are unresolved. */
  individual_payment_total?: number | null;
  /** `subs_payment` (an hourly rate) x `hours_worked`. Null when hours are unresolved. */
  subs_payment_total?: number | null;
  created_at: timestamp;
  updated_at: timestamp;
}

export type daily_work_hours_source = "clock" | "scheduled" | "manual";

export type daily_work_payout_review = "missing_hours" | "invalid_span" | "crossed_midnight" | "over_scheduled";

export interface DailyWorkExecutionTask {
  name: string;
  done: boolean;
  completed_at?: string | null;
}

export interface DailyWorkExecution {
  id: uuid;
  daily_work_day: uuid | null;
  clock_in: string | null; // TIME format HH:MM:SS
  clock_out: string | null; // TIME format HH:MM:SS
  tasks: DailyWorkExecutionTask[]; // JSONB array
  notes: string | null;
  created_at: timestamp;
  updated_at: timestamp;
}

export enum daily_work_log_type {
  create = "create",
  update = "update",
  delete = "delete",
  task_assigned = "task_assigned",
  task_unassigned = "task_unassigned",
  clock_in = "clock_in",
  clock_out = "clock_out",
  task_completed = "task_completed",
  status_change = "status_change",
}

export interface DailyWorkLog {
  id: uuid;
  daily_work: uuid;
  type: daily_work_log_type;
  message: string;
  meta_data: Record<string, any> | null;
  user: uuid | null;
  created_at: timestamp;
  updated_at: timestamp;
}
